import { Button, Loader, Notification } from "@stellar/design-system";

import { DialogModal } from "@/components/DialogModal";

import { CO_SIGNING_MAX_SIGNERS } from "@/constants/settings";

import { getEnrollmentErrorMessage } from "@/helpers/enrollmentErrors";

import { useInviteeOwnerFlow } from "../hooks/useInviteeOwnerFlow";

import { CoSignerKeyStep } from "./CoSignerKeyStep";
import { ReviewDetails } from "./ReviewDetails";

import type { MultisigEnrollmentSession } from "@/types";

type InviteeOwnerFlowProps = {
  orgName: string;
  currentUserId: string;
  activeSession?: MultisigEnrollmentSession | null;
  onBack: () => void;
  onDone: () => void;
  onGoToTeamMembers: () => void;
};

export const InviteeOwnerFlow = ({
  orgName,
  currentUserId,
  activeSession = null,
  onBack,
  onDone,
  onGoToTeamMembers,
}: InviteeOwnerFlowProps) => {
  const {
    currentStep,
    goToQr,
    goToReview,
    handleBack,

    keyResult,
    challengeXdr,
    handleKeyConnected,
    handleKeyDisconnected,
    disconnectWalletRef,
    signRef,

    signRequestPending,
    signError,
    canConfirm,
    handleConfirm,

    isBlocked,
    cancelExit,
    handleConfirmExit,

    handleDone,
    successHeading,
    successBody,

    owner1Name,
    owner1Email,
    youName,
    youEmail,

    submitKeyMutation,
    verifyKeyMutation,
  } = useInviteeOwnerFlow({ orgName, activeSession, onBack, onDone });

  if (currentStep === "intro") {
    return (
      <div className="MultisigSetup__card MultisigSetup__card--intro">
        <h2 className="MultisigSetup__title">Finish co-signing setup for {orgName}</h2>
        <p className="MultisigSetup__p">
          Owner 1 is setting up co-signing for {orgName} and you&apos;re the second Owner.
        </p>
        <p className="MultisigSetup__p">
          Today, one person can send a disbursement on their own. With co-signing, every
          disbursement must be signed by two co-signers in VIA Wallet before funds go out.
        </p>
        <p className="MultisigSetup__p">
          To finish, you&apos;ll set up your signing key in VIA Wallet on your own device.
        </p>
        <p className="MultisigSetup__p">
          <strong>Co-signing turns on for your organization as soon as you finish setup.</strong>
        </p>
        <div className="MultisigSetup__actions">
          <Button variant="primary" size="md" onClick={goToQr}>
            Continue
          </Button>
          <Button variant="tertiary" size="md" onClick={onBack}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="MultisigSetup__card">
        {submitKeyMutation.isPending && currentStep === "qr" && (
          <div className="Notification__warning">
            <Notification variant="primary" title="Registering your co-signing key…" isFilled>
              Sending your public key to the server. This usually takes a moment.
            </Notification>
          </div>
        )}
        {submitKeyMutation.isError && currentStep === "qr" && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't register your co-signing key" isFilled>
              {getEnrollmentErrorMessage(submitKeyMutation.error)} Disconnect the wallet and scan
              the code again to retry.
            </Notification>
          </div>
        )}
        {currentStep === "review" && signRequestPending && (
          <div className="Notification__warning">
            <Notification
              variant="primary"
              title="Approve the signing request in VIA Wallet"
              isFilled
            >
              We sent a signing request to your paired wallet. Confirm it there to finish setting up
              your co-signing key.
            </Notification>
          </div>
        )}
        {currentStep === "review" && signError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't sign in VIA Wallet" isFilled>
              {signError}
            </Notification>
          </div>
        )}
        {currentStep === "review" && verifyKeyMutation.isError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't verify your co-signing key" isFilled>
              {getEnrollmentErrorMessage(verifyKeyMutation.error)}
            </Notification>
          </div>
        )}

        {(currentStep === "qr" || currentStep === "review") && (
          // Keep CoSignerKeyStep mounted across QR ↔ Review so the WalletConnect
          // session survives back-and-forth navigation. Hide it visually while
          // Review is active. Its unmount cleanup disconnects the WC session,
          // so moving to "success" on Confirm naturally closes the pairing
          // before the Done button.
          <div style={{ display: currentStep === "qr" ? undefined : "none" }}>
            <CoSignerKeyStep
              userId={currentUserId || "sdp-multisig-owner2"}
              onConnected={handleKeyConnected}
              onDisconnected={handleKeyDisconnected}
              disconnectRef={disconnectWalletRef}
              signRef={signRef}
            />
          </div>
        )}

        {currentStep === "review" && (
          <>
            <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
              Review
            </h2>
            <p className="MultisigSetup__p">
              When you confirm, co-signing turns on for {orgName}. From then on, every disbursement
              will need two co-signers before funds go out, and no one can send funds alone.
            </p>
            <hr className="MultisigSetup__rule" />
            <ReviewDetails
              owner1={{ name: owner1Name, email: owner1Email }}
              owner2={{ name: youName, email: youEmail }}
              youOwner={2}
              publicKey={keyResult?.publicKey ?? ""}
            />
          </>
        )}

        {currentStep === "success" && (
          <>
            <h2 className="MultisigSetup__title MultisigSetup__title--ok">
              <span className="MultisigConnect__check MultisigConnect__check--lg" aria-hidden>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  name="check"
                >
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
              </span>
              {successHeading}
            </h2>
            <p className="MultisigSetup__p">{successBody}</p>
            <hr className="MultisigSetup__rule" />
            <ReviewDetails
              owner1={{ name: owner1Name, email: owner1Email }}
              owner2={{ name: youName, email: youEmail }}
              youOwner={2}
              publicKey={keyResult?.publicKey ?? verifyKeyMutation.data?.owner2_public_key ?? ""}
            />
          </>
        )}
      </div>

      {currentStep === "success" && (
        <div className="MultisigSetup__card">
          <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
            Add more co-signers
          </h2>
          <p className="MultisigSetup__p">
            You can add up to {CO_SIGNING_MAX_SIGNERS} co-signers once co-signing is on. Only team
            members with the Financial Controller or Owner role can be co-signers, so give them that
            role first in Team members.
          </p>
        </div>
      )}

      <div
        className={`MultisigSetup__footer${currentStep === "success" ? " MultisigSetup__footer--end" : ""}`}
      >
        {currentStep !== "success" && (
          <Button variant="tertiary" size="md" onClick={handleBack}>
            ‹ Back
          </Button>
        )}

        {currentStep === "success" ? (
          <div className="MultisigSetup__footerActions">
            <Button variant="tertiary" size="md" onClick={onGoToTeamMembers}>
              Go to team members
            </Button>
            <Button variant="primary" size="md" onClick={handleDone}>
              Done
            </Button>
          </div>
        ) : currentStep === "review" ? (
          <Button
            variant="primary"
            size="md"
            onClick={handleConfirm}
            disabled={!canConfirm}
            icon={
              signRequestPending || verifyKeyMutation.isPending ? (
                <Loader size="0.85rem" />
              ) : undefined
            }
            iconPosition="left"
          >
            {signRequestPending
              ? "Waiting for VIA Wallet…"
              : verifyKeyMutation.isPending
                ? "Verifying key…"
                : "Confirm"}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={goToReview}
            disabled={!keyResult || !challengeXdr || submitKeyMutation.isPending}
            icon={submitKeyMutation.isPending ? <Loader size="0.85rem" /> : undefined}
            iconPosition="left"
          >
            {submitKeyMutation.isPending ? "Registering key…" : "Next"}
          </Button>
        )}
      </div>

      <DialogModal
        visible={isBlocked}
        width="50"
        onClose={cancelExit}
        title="Exit before confirming?"
        footerAlignment="stretch"
        primaryAction={{
          label: "Keep setting up",
          variant: "tertiary",
          onClick: cancelExit,
        }}
        secondaryAction={{
          label: "Exit without confirming",
          variant: "destructive",
          onClick: handleConfirmExit,
        }}
      >
        <p>
          You've set up your co-signing key, but co-signing won't turn on until you confirm. You can
          come back to finish anytime.
        </p>
      </DialogModal>
    </>
  );
};
